import { Person } from "@/api/models/Person";
import { PersonTypeEnum } from "@/autogen";
import { useLookupStore } from "@/stores/lookups";
type OptionalBooleanFlags<T> = {
  [K in keyof T]?: boolean;
};
export type PersonFieldMask = OptionalBooleanFlags<Person>;
export interface iPersonTypeMask {
  requiredFields?: string[];
  personType: PersonTypeEnum | undefined;
  minPhones: number;
  minAddresses: number;
  minEmergencyContacts: number;
  setPersonType: (newType: PersonTypeEnum | undefined) => void;
  isOrganization: () => boolean;
  disabledPersonFields: PersonFieldMask;
  showPersonData: {
    type: boolean;
    contact: boolean;
    address: boolean;
    phone: boolean;
    contactInformation: boolean;
    emergencyContacts: boolean;
    demographics: boolean;
    specialNeeds: boolean;
    experience: boolean;
    employment: boolean;
    other: boolean;
    school: boolean;
  };
  showPersonField: {
    email: boolean;
    type: boolean;
    name: boolean;
    organization: boolean;
    dob: boolean;
    parentEmail: boolean;
    nameAdds: boolean;
    phone: boolean;
    demographicsMain: boolean;
    school: boolean;
    veteran: boolean;
    gender: boolean;
    reducedLunch: boolean;
    military: boolean;
    referral: boolean;
    sailingExp: boolean;
    deceased: boolean;
  };
  fieldNameOverride?: string;
}

type compNames = keyof iPersonTypeMask["showPersonData"];
type fieldNames = keyof iPersonTypeMask["showPersonField"];

const allComponents: compNames[] = [
  "contact",
  "address",
  "emergencyContacts",
  "demographics",
  "specialNeeds",
  "experience",
  "employment",
  "other",
  "school",
];

// map of components to persontype
const componentMap: Record<PersonTypeEnum, string[]> = {
  [PersonTypeEnum.ADULT_MEMBER]: allComponents,
  [PersonTypeEnum.JUNIOR_MEMBER]: [
    ...allComponents.filter((c) => c !== "employment"),
    "parentEmail",
  ],
  [PersonTypeEnum.UAP_MEMBER]: [...allComponents, "parentEmail"],
  [PersonTypeEnum.ORGANIZATION]: [
    "organization",
    "contact",
    "address",
    "demographics",
    "experience",
  ],
  [PersonTypeEnum.ORGANIZATION_CONTACT]: [
    "organization",
    "contact",
    "address",
    "demographics",
    "experience",
    "other",
  ],
  [PersonTypeEnum.FRIEND]: allComponents,
  [PersonTypeEnum.JUNIOR_PARENT]: allComponents,
  [PersonTypeEnum.ADULT_GUEST]: allComponents.filter(
    (c) => !["address", "employment"].includes(c),
  ),
  [PersonTypeEnum.EMERGENCY_CONTACT]: allComponents.filter(
    (c) => !["address", "employment"].includes(c),
  ),
  [PersonTypeEnum.JP_INSTRUCTOR]: allComponents.filter(
    (c) => !["address", "employment"].includes(c),
  ),
};

const condFields: fieldNames[] = [
  "organization",
  "dob",
  "phone",
  "parentEmail",
  "nameAdds",
  "name",
  "demographicsMain",
  "school",
  "reducedLunch",
  "military",
  "gender",
  "veteran",
  "referral",
  "sailingExp",
  "deceased",
];

// map of fields to persontype
const fieldMap: Record<PersonTypeEnum, string[]> = {
  [PersonTypeEnum.ADULT_MEMBER]: [
    ...condFields.filter(
      (c) => !["organization", "parentEmail", "reducedLunch"].includes(c),
    ),
  ],
  [PersonTypeEnum.JUNIOR_MEMBER]: [
    ...condFields.filter((c) => !["organization", "military"].includes(c)),
  ],
  [PersonTypeEnum.UAP_MEMBER]: [
    ...condFields.filter((c) => !["organization", "reducedLunch"].includes(c)),
  ],
  [PersonTypeEnum.ORGANIZATION]: [
    "nameAdds",
    "organization",
    "military",
    "phone",
  ],
  [PersonTypeEnum.ORGANIZATION_CONTACT]: [
    "organization",
    "dob",
    "gender",
    "phone",
  ],
  [PersonTypeEnum.FRIEND]: [...condFields],
  [PersonTypeEnum.JUNIOR_PARENT]: [...condFields],
  [PersonTypeEnum.ADULT_GUEST]: ["dob", "veteran"],
  [PersonTypeEnum.EMERGENCY_CONTACT]: [...condFields],
  [PersonTypeEnum.JP_INSTRUCTOR]: [...condFields],
};

/**
 * person type mask used to show/hide components and fields based on person type
 * @param initialPersonType
 */
export function usePersonTypeMask(
  initialPersonType?: PersonTypeEnum,
): iPersonTypeMask {
  const lookupStore = useLookupStore();

  const personType = ref<PersonTypeEnum | undefined>(initialPersonType);

  const disabledPersonFields = reactive<PersonFieldMask>({});

  const showPersonData = reactive<Record<compNames, boolean>>({
    type: false,
    contact: false,
    address: true,
    phone: true,
    contactInformation: true,
    emergencyContacts: false,
    demographics: true,
    specialNeeds: false,
    experience: false,
    employment: false,
    other: false,
    school: false,
  });
  const showPersonField = reactive<Record<fieldNames, boolean>>({
    type: false,
    name: false,
    organization: false,
    dob: false,
    parentEmail: false,
    email: true,
    phone: false,
    nameAdds: false,
    demographicsMain: true,
    school: false,
    veteran: false,
    gender: false,
    reducedLunch: false,
    military: false,
    referral: false,
    sailingExp: false,
    deceased: false,
  });

  const personTypeName = computed(() => {
    if (personType.value) {
      if (lookupStore.findPersonType(personType.value)) {
        return lookupStore.findPersonType(personType.value)?.name;
      }
    }
    return "";
  });

  const visibleComponents = computed<string[]>(() =>
    personType.value && componentMap[personType.value]
      ? componentMap[personType.value]
      : allComponents,
  );

  const visibleFields = computed<string[]>(() =>
    personType.value && fieldMap[personType.value]
      ? fieldMap[personType.value]
      : condFields,
  );

  allComponents.forEach((component) => {
    if (visibleComponents.value.includes(component)) {
      showPersonData[component] = true;
    }
  });

  condFields.forEach((field) => {
    if (visibleFields.value.includes(field)) {
      showPersonField[field] = true;
    }
  });

  function setPersonType(newType: PersonTypeEnum | undefined) {
    personType.value = newType;
    allComponents.forEach((component) => {
      if (visibleComponents.value.includes(component)) {
        showPersonData[component] = true;
      } else {
        showPersonData[component] = false;
      }
    });

    condFields.forEach((field) => {
      if (visibleFields.value.includes(field)) {
        showPersonField[field] = true;
      } else {
        showPersonField[field] = false;
      }
    });
  }

  function isOrganization() {
    if (personTypeName.value) {
      return (
        personTypeName.value === "Organization" ||
        personTypeName.value === "Organizaton"
      );
    }
    return false;
  }

  // initialize
  setPersonType(initialPersonType);

  return reactive({
    personType: personType.value,
    minAddresses: 1,
    minPhones: personType.value == PersonTypeEnum.JUNIOR_MEMBER ? 0 : 1,
    minEmergencyContacts: 0,
    setPersonType,
    isOrganization,
    disabledPersonFields,
    showPersonData,
    showPersonField,
  });
}
